import { c as _c } from "react/compiler-runtime";
import { useRefreshSeatUsage } from "@components/table/members/assign-seats/use-refresh-seat-usage";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast, Text, NO_TRANSLATE_CLASS } from "@flat-ui";
import { setMemberPermissionsInCompany, updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { fetchActiveMembers } from "@store/active-members/active-members-slice-thunk";
import { releaseAssignedXgSeat } from "@store/sdb-company/sdb-company-slice";
import { useAppDispatch } from "@store/store-helper";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName } from "@utils/user-utils";
/**
 * Returns how to take the Sphere XG seat of a member away. An admin is given a workspace role that
 * needs no seat first.
 *
 * An assigned seat is the `activeSeat` permission of the member, which is removed. The seat of an
 * active user is already freed once they hold no role that needs it, so the active members are only
 * fetched again to show that.
 *
 * @param seatKind The kind of seat to take away. Default "assigned"
 */
export function useRemoveXgSeat(t0) {
  const $ = _c(8);
  const seatKind = t0 === undefined ? "assigned" : t0;
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const refreshSeatUsage = useRefreshSeatUsage();
  let t1;
  if ($[0] !== companyId || $[1] !== dispatch || $[2] !== handleErrorWithToast || $[3] !== openToast || $[4] !== refreshSeatUsage || $[5] !== seatKind || $[6] !== trackEvent) {
    const removeXgSeat = async function removeXgSeat(member, newWorkspaceRole) {
      trackEvent({
        name: WorkspaceEvents.unassignXgSeat,
        props: {
          location: "workspace-members-table",
          seatKind,
          newWorkspaceRole: newWorkspaceRole ?? "unchanged"
        }
      });
      if (newWorkspaceRole && !(await changeWorkspaceRole(member, newWorkspaceRole))) {
        return false;
      }
      ;
      try {
        if (seatKind === "assigned") {
          await removeAssignedSeat(member);
        } else {
          await dispatch(fetchActiveMembers({
            companyId,
            shouldForceFetch: true
          })).unwrap();
        }
        openToast({
          title: "Sphere XG seat removed",
          message: <>Successfully removed the seat for{" "}<Text variant="bodyM" sx={{
              fontWeight: "bold"
            }} className={NO_TRANSLATE_CLASS}>{getPrettyName(member)}</Text></>
        });
        return true;
      } catch (t2) {
        const error = t2;
        handleErrorWithToast({
          id: `unassignXgSeat-${Date.now().toString()}`,
          title: "Cannot remove the Sphere XG seat",
          error
        });
        return false;
      }
    };
    async function removeAssignedSeat(member_0) {
      await dispatch(setMemberPermissionsInCompany({
        companyId,
        userIdentity: member_0.identity,
        permissions: [{
          name: "activeSeat",
          allowed: false
        }]
      })).unwrap();
      dispatch(releaseAssignedXgSeat());
      refreshSeatUsage();
    }
    async function changeWorkspaceRole(member_1, role) {
      try {
        await dispatch(updateMemberRoleInWorkspace({
          companyId,
          role,
          identity: member_1.identity
        })).unwrap();
        return true;
      } catch {
        return false;
      }
    }
    t1 = {
      removeXgSeat
    };
    $[0] = companyId;
    $[1] = dispatch;
    $[2] = handleErrorWithToast;
    $[3] = openToast;
    $[4] = refreshSeatUsage;
    $[5] = seatKind;
    $[6] = trackEvent;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}